<i18n>
{
  "fr": {
    "configuratorAddToCartUi": {
      "steps": {
        "export": "Export de votre personnalisation",
        "upload": {
          "label": "Envoi de votre personnalisation",
          "progress": "({progress}%)"
        },
        "addToCart": "Ajout au panier"
      },
      "error": "Une erreur est survenue, merci de ressayer.\n\nSi le problème persiste, merci de nous contacter via le formulaire prévu à cet effet."
    }
  }
}
</i18n>

<template>
  <div class="configurator-add-to-cart-ui">
    <ul v-if="!isProject" class="configurator-add-to-cart-ui__step-list">
      <ConfiguratorAddToCartUiStep :status="stepStatus1" tag="li">
        {{ $t('configuratorAddToCartUi.steps.export') }}
      </ConfiguratorAddToCartUiStep>

      <ConfiguratorAddToCartUiStep :status="stepStatus2" tag="li">
        {{ $t('configuratorAddToCartUi.steps.upload.label') }}
        <template v-if="stepStatus2 > StepStatus.IDLE">
          {{
            $t('configuratorAddToCartUi.steps.upload.progress', { progress })
          }}
        </template>
      </ConfiguratorAddToCartUiStep>

      <ConfiguratorAddToCartUiStep :status="stepStatus3" tag="li">
        {{ $t('configuratorAddToCartUi.steps.addToCart') }}
      </ConfiguratorAddToCartUiStep>
    </ul>

    <ConfiguratorTextureCanvasEnhancer
      v-if="canRenderTexture"
      ref="exportTexture"
      :resolution="resolution"
      headless
    />

    <template v-if="canRender3d">
      <ConfiguratorTextureCanvasEnhancer
        ref="texture"
        :resolution="Resolution.HIGH_DPI"
        full-texture
        headless
        include-background
        @change="updateTexture"
      />

      <div class="configurator-add-to-cart-ui__export-3d-view">
        <Configurator3dView
          v-if="texture"
          ref="canvas"
          :gltf="gltfUrl"
          :style="{ width: '600px', height: '1000px' }"
          :texture="texture"
          :camera-position="cameraPosition"
          headless
        />
      </div>
    </template>
  </div>
</template>

<script>
import {
  ConfiguratorAction,
  ConfiguratorGetters,
  ConfiguratorMutation,
  mapGetters as mapConfiguratorGetters,
  prefixType as prefixConfiguratorType,
} from '../../store/modules/configurator';
import ConfiguratorAddToCartUiStep, {
  StepStatus,
} from './ConfiguratorAddToCartUiStep';
import ConfiguratorTextureCanvasEnhancer from './ConfiguratorTextureCanvasEnhancer';
import { Resolution } from '../../utils/MathUtils';
import Configurator3dView from './Configurator3dView';
import {
  addToCart,
  requestToken,
  updateOrder,
  uploadFile,
  saveCustomerProject,
} from '../../api/api';
import { ZoneType } from '../../api/static/zones';
import { CameraPosition } from '../../api/static/sizes';
import { delay } from '../../utils/TimeUtils';
import { objectUrlToBlob } from '../../utils/ImageUtils';
import { PrintTypeId } from '../../api/static/printTypes';
import { mapState as mapMiscState } from '../../store/modules/configurator/misc';
import PromiseHelper from '../../utils/PromiseHelper';

const ExportKey = Object.freeze({
  TEXTURE_72: 0,
  TEXTURE_300: 1,
  RENDER_1: 2,
  RENDER_2: 3,
  RENDER_3: 4,
  RENDER_4: 5,
});

export default {
  name: 'ConfiguratorAddToCartUi',
  components: {
    Configurator3dView,
    ConfiguratorTextureCanvasEnhancer,
    ConfiguratorAddToCartUiStep,
  },
  props: {
    isProject: Boolean,
  },
  data() {
    return {
      uploadProgress: 0,
      stepStatus1: StepStatus.IDLE,
      stepStatus2: StepStatus.IDLE,
      stepStatus3: StepStatus.IDLE,
      resolution: Resolution.HIGH_DPI,
      canRenderTexture: false,
      canRender3d: false,
      texture: null,
      StepStatus,
      Resolution,
      progressMap: {
        [ExportKey.TEXTURE_72]: 0,
        [ExportKey.TEXTURE_300]: 0,
        [ExportKey.RENDER_1]: 0,
        [ExportKey.RENDER_2]: 0,
        [ExportKey.RENDER_3]: 0,
        [ExportKey.RENDER_4]: 0,
      },
      exportMap: {
        [ExportKey.TEXTURE_72]: null,
        [ExportKey.TEXTURE_300]: null,
        [ExportKey.RENDER_1]: null,
        [ExportKey.RENDER_2]: null,
        [ExportKey.RENDER_3]: null,
        [ExportKey.RENDER_4]: null,
      },
      cameraPositionKey: CameraPosition.CENTER,
      promiseHelper: new PromiseHelper(),
    };
  },
  computed: {
    ...mapConfiguratorGetters({
      activeProductVariant: ConfiguratorGetters.GET_ACTIVE_PRODUCT_VARIANT,
      activeCustomizations: ConfiguratorGetters.GET_ACTIVE_ZONE_CUSTOMIZATIONS,
      activeSize: ConfiguratorGetters.GET_ACTIVE_SIZE,
      activeProduct: ConfiguratorGetters.GET_ACTIVE_PRODUCT,
      activeScreenPrintingColor:
        ConfiguratorGetters.GET_ACTIVE_SCREEN_PRINTING_COLOR,
      selectedProductIndex: ConfiguratorGetters.GET_SELECTED_PRODUCT_INDEX,
      activeCategory: ConfiguratorGetters.GET_ACTIVE_CATEGORY,
      activeCategoryPathName: ConfiguratorGetters.GET_ACTIVE_CATEGORY_PATH_NAME,
    }),
    ...mapMiscState(['editOrderItemUrl']),
    gltfUrl() {
      const activeSizeId = this.$store.state.configurator.activeSizeId;
      const sizeMap = this.$store.state.configurator.glasses.sizeMap;

      return (
        (activeSizeId &&
          sizeMap &&
          sizeMap[activeSizeId] &&
          sizeMap[activeSizeId].gltfUrl) ||
        null
      );
    },
    progress() {
      const list = Object.values(this.progressMap);
      const progressSum = list.reduce((acc, sb) => {
        acc = acc + sb;

        return acc;
      }, 0);

      const progress = progressSum / list.length;

      return progress.toFixed();
    },
    cameraPosition() {
      return (
        this.activeSize &&
        this.activeSize.cameraPosition &&
        this.activeSize.cameraPosition[this.cameraPositionKey]
      );
    },
    isExpressDeliveryEnabled() {
      return this.$store.state.configurator.isExpressDeliveryEnabled;
    },
  },
  created() {
    this.promiseHelper.request(ExportKey.RENDER_1);
    this.promiseHelper.request(ExportKey.RENDER_2);
    this.promiseHelper.request(ExportKey.RENDER_3);
    this.promiseHelper.request(ExportKey.RENDER_4);
  },
  mounted() {
    this.export();
  },
  methods: {
    async export() {
      try {
        // Step 1
        try {
          this.canRenderTexture = true;
          this.stepStatus1 = StepStatus.PENDING;
          await this.$nextTick();

          // Texture exports
          this.exportMap[ExportKey.TEXTURE_72] = await this.getTexture72Dpi();
          this.exportMap[ExportKey.TEXTURE_300] = await this.getTexture300Dpi();

          this.canRenderTexture = false;
          this.canRender3d = true;

          // 3D exports
          console.groupCollapsed('3D exports');

          await this.$nextTick();
          await this.updateTexture();

          this.cameraPositionKey = CameraPosition.CENTER;
          await this.$nextTick();
          const blobExport = await this.$refs.canvas.toBlob();
          this.exportMap[ExportKey.RENDER_2] = blobExport;
          this.promiseHelper.resolve(ExportKey.RENDER_2, blobExport);

          this.cameraPositionKey = CameraPosition.LEFT;
          await this.$nextTick();
          const blobExportLeft = await this.$refs.canvas.toBlob();
          this.exportMap[ExportKey.RENDER_1] = blobExportLeft;
          this.promiseHelper.resolve(ExportKey.RENDER_1, blobExportLeft);

          this.cameraPositionKey = CameraPosition.RIGHT;
          await this.$nextTick();
          const blobExportRight = await this.$refs.canvas.toBlob();
          this.exportMap[ExportKey.RENDER_3] = blobExportRight;
          this.promiseHelper.resolve(ExportKey.RENDER_3, blobExportRight);

          this.cameraPositionKey = CameraPosition.BACK;
          await this.$nextTick();
          const blobExportBack = await this.$refs.canvas.toBlob();
          this.exportMap[ExportKey.RENDER_4] = blobExportBack;
          this.promiseHelper.resolve(ExportKey.RENDER_4, blobExportBack);

          this.canRender3d = false;
          this.stepStatus1 = StepStatus.DONE;

          console.groupEnd();
        } catch (e) {
          console.groupEnd();

          this.stepStatus1 = StepStatus.ERROR;

          throw e;
        }

        // Step 2
        let token;
        const fileIdMap = {};

        try {
          this.stepStatus2 = StepStatus.PENDING;
          token = await requestToken();

          // Store CSRF token in Vuex store for cross-sell reuse
          this.$store.commit(
            prefixConfiguratorType(ConfiguratorMutation.SET_CSRF_TOKEN),
            token
          );

          let promiseChain = Promise.resolve();

          // Upload exports
          [
            ExportKey.TEXTURE_72,
            ExportKey.TEXTURE_300,
            ExportKey.RENDER_1,
            ExportKey.RENDER_2,
            ExportKey.RENDER_3,
            ExportKey.RENDER_4,
          ].forEach(fileKey => {
            promiseChain = promiseChain
              .then(() =>
                uploadFile(this.exportMap[fileKey], token, progressEvent => {
                  this.progressMap[fileKey] =
                    (progressEvent.loaded * 100) / progressEvent.total;
                })
              )
              .then(fileId => {
                fileIdMap[fileKey] = fileId;
                // Free blob from memory once uploaded
                this.exportMap[fileKey] = null;
              });
          });

          // Upload zone images
          this.activeCustomizations.forEach(({ customization }) => {
            if (customization.type !== ZoneType.IMAGE || !customization.url) {
              return;
            }

            const fileKey = this.getUserFileFromCustomization(customization);

            promiseChain = promiseChain
              .then(() => objectUrlToBlob(customization.url))
              .then(blob => {
                return uploadFile(blob, token, progressEvent => {
                  this.$set(
                    this.progressMap,
                    fileKey,
                    (progressEvent.loaded * 100) / progressEvent.total
                  );
                });
              })
              .then(fileId => {
                fileIdMap[fileKey] = fileId;
              });
          });

          await promiseChain;

          this.stepStatus2 = StepStatus.DONE;
        } catch (e) {
          this.stepStatus2 = StepStatus.ERROR;

          throw e;
        }

        // Step 3
        try {
          this.stepStatus3 = StepStatus.PENDING;

          /** @type {ConfiguratorState} */
          const configuratorState = this.$store.state.configurator;

          const data = {
            configurationId: this.activeProductVariant.configurationId,
            variantId: this.activeProductVariant.id,
            colorId: configuratorState.activeColorId,
            fileHighQualityId: fileIdMap[ExportKey.TEXTURE_300],
            fileLowQualityId: fileIdMap[ExportKey.TEXTURE_72],
            exportFiles: [
              fileIdMap[ExportKey.RENDER_1],
              fileIdMap[ExportKey.RENDER_2],
              fileIdMap[ExportKey.RENDER_3],
              fileIdMap[ExportKey.RENDER_4],
            ],
            screenPrintingColorId:
              (this.activeProductVariant.printTypeId ===
                PrintTypeId.SCREEN_PRINTING &&
                this.activeScreenPrintingColor &&
                this.activeScreenPrintingColor.id) ||
              null,
            customizations: [
              ...this.activeCustomizations.reduce(
                (acc, { zone, customization }) => {
                  if (
                    customization.text ||
                    customization.url ||
                    zone.defaultValue
                  ) {
                    const isProductCustomizable =
                      this.activeProduct.isCustomizable;
                    const o = {
                      zoneId: isProductCustomizable ? null : zone.id,
                      templateZoneId: zone.id,
                      groupId: zone.groupId,
                      x: zone.x,
                      y: zone.y,
                      scale: zone.scale, // Handle scaleX or scaleY
                      rotation: zone.rotation,
                      color: zone.color,
                    };

                    if (zone.type === ZoneType.TEXT) {
                      o.text =
                        customization.text !== undefined
                          ? customization.text
                          : zone.defaultValue;

                      if (isProductCustomizable) {
                        Object.assign(o, {
                          fontSize: customization.fontSize || zone.fontSize,
                          bold: customization.bold || false,
                          italic: customization.italic || false,
                          underline: customization.underlined || false,
                          typoId: customization.typoId || null,
                          innerX: customization.x || 0,
                          innerY: customization.y || 0,
                          innerScale: customization.scaleX || 1, // ScaleX and scaleY are identical as we preserve ratio
                          innerRotation: customization.rotation || 0,
                          color: customization.color || zone.color || null,
                        });
                      } else {
                        Object.assign(o, {
                          fontSize: zone.fontSize,
                          bold: zone.bold,
                          italic: zone.italic,
                          underline: zone.underlined,
                          typoId: zone.typoId,
                        });
                      }
                    } else if (zone.type === ZoneType.IMAGE) {
                      const fileId =
                        fileIdMap[
                          this.getUserFileFromCustomization(customization)
                        ];

                      // Skip image zones without an uploaded file
                      if (!fileId) {
                        return acc;
                      }

                      Object.assign(o, {
                        fileId,
                        badQuality: customization.badQuality,
                      });

                      if (isProductCustomizable) {
                        Object.assign(o, {
                          innerX: customization.x || 0,
                          innerY: customization.y || 0,
                          innerScale: customization.scaleX || 1, // ScaleX and scaleY are identical as we preserve ratio
                          innerRotation: customization.rotation || 0,
                          innerWidth: customization.width || 0,
                          innerHeight: customization.height || 0,
                        });
                      }
                    } else {
                      throw new Error('Unknown zone type');
                    }

                    acc.push(o);
                  }

                  return acc;
                },
                []
              ),
            ],
            expressMode: this.isExpressDeliveryEnabled,
          };

          if (this.isProject) {
            if (configuratorState.currentCustomerProjectId) {
              data.customerProjectId =
                configuratorState.currentCustomerProjectId;
            }

            const response = await saveCustomerProject(data, token);

            if (response.data.customerProjectId) {
              await this.$store.dispatch(
                prefixConfiguratorType(ConfiguratorAction.SET_ACTIVE_STATE),
                {
                  customerProjectId: response.data.customerProjectId,
                  isCustomerProjectDirty: false,
                }
              );
            }
          } else {
            if (configuratorState.currentCartItemId) {
              data.cartItemId = configuratorState.currentCartItemId;
            }

            if (configuratorState.currentOrderItemId) {
              data.orderItemId = configuratorState.currentOrderItemId;

              const url = this.editOrderItemUrl;

              if (!url) {
                throw new Error('Order edit URL is undefined.');
              }

              await updateOrder(url, data, token);
            } else {
              const response = await addToCart(data, token);

              if (response.data.removeFromCart) {
                globalThis.dispatchEvent(
                  new CustomEvent('lgf.removeFromCart', {
                    detail: {
                      ecommerce: {
                        items: [response.data.removeFromCart.item],
                      },
                    },
                  })
                );
              }

              globalThis.dispatchEvent(
                new CustomEvent('lgf.addToCart', {
                  detail: {
                    ecommerce: {
                      items: [
                        {
                          ...response.data.addToCart.item,
                          index: this.selectedProductIndex,
                          item_list_name: this.activeCategoryPathName,
                          item_list_id: this.activeCategory
                            ? this.activeCategory.id
                            : null,
                        },
                      ],
                    },
                  },
                })
              );

              const url = new URL(globalThis.location);
              url.searchParams.set(
                'cartItemId',
                response.data.addToCart.item.id
              );
              globalThis.history.replaceState({}, '', url);
            }
          }

          this.stepStatus3 = StepStatus.DONE;
        } catch (e) {
          this.stepStatus3 = StepStatus.ERROR;

          throw e;
        }

        this.$emit('success');
      } catch (e) {
        console.error(e);

        await delay(100);

        alert(this.$t('configuratorAddToCartUi.error'));

        this.$emit('error');
      }
    },
    getUserFileFromCustomization({ id }) {
      return `user-file--${id}`;
    },
    /**
     *
     * @return {Promise<Blob>}
     */
    async textureComponentToBlob() {
      return this.$refs.exportTexture.toBlob();
    },
    /**
     *
     * @return {Promise<Blob>}
     */
    async getTexture72Dpi() {
      if (this.resolution !== Resolution.WEB) {
        this.resolution = Resolution.WEB;

        await this.$nextTick();
      }

      return this.textureComponentToBlob();
    },
    /**
     *
     * @return {Promise<Blob>}
     */
    async getTexture300Dpi() {
      if (this.resolution !== Resolution.PRINT) {
        this.resolution = Resolution.PRINT;

        await this.$nextTick();
      }

      return this.textureComponentToBlob();
    },
    async updateTexture() {
      this.texture = await this.$refs.texture.toBase64();
    },
    /**
     * Returns the CENTER thumbnail URL (legacy method for backward compatibility)
     * @return {Promise<string>}
     */
    getPreviewThumbnailUrl() {
      return this.promiseHelper
        .get(ExportKey.RENDER_2)
        .then(imageBlob => URL.createObjectURL(imageBlob));
    },
    /**
     * Returns all 4 view thumbnail URLs (center, left, right, back).
     * Blobs are resolved sequentially to reduce peak memory usage on low-end devices (e.g. Safari iOS).
     * @return {Promise<{center: string, left: string, right: string, back: string}>}
     */
    async getPreviewThumbnails() {
      const centerBlob = await this.promiseHelper.get(ExportKey.RENDER_2);
      const leftBlob = await this.promiseHelper.get(ExportKey.RENDER_1);
      const rightBlob = await this.promiseHelper.get(ExportKey.RENDER_3);
      const backBlob = await this.promiseHelper.get(ExportKey.RENDER_4);

      return {
        center: URL.createObjectURL(centerBlob),
        left: URL.createObjectURL(leftBlob),
        right: URL.createObjectURL(rightBlob),
        back: URL.createObjectURL(backBlob),
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.configurator-add-to-cart-ui {
  &__step-list {
    list-style: none;
  }

  &__step-item {
    display: flex;
  }

  &__export-3d-view {
    @include sr-only();
  }
}
</style>
